import type { Media } from "@/payload-types";

/** One pre-generated rendition of a Media upload, usable as a `srcset` candidate. */
export type MediaVariant = { url: string; width: number };

/**
 * Payload sizes that are cropped to a different aspect ratio than the original.
 * They cannot stand in for the original in a `srcset`, so they are never variants.
 */
const CROPPED_SIZES = new Set(["og", "square"]);

/**
 * Collect the width-preserving renditions of a Media upload, sorted by width ascending
 * and deduped by url. The original upload is only a candidate when no generated size
 * qualifies (e.g. tiny originals below the smallest size) — otherwise it is skipped so a
 * heavy, possibly non-webp original is never served from a `srcset`.
 * Returns `[]` for unpopulated relations and uploads with no url+width rendition.
 */
export function getMediaVariants(media: Media | number | null | undefined): MediaVariant[] {
    if (!media || typeof media === "number") return [];

    const candidates: MediaVariant[] = [];
    for (const [name, size] of Object.entries(media.sizes ?? {})) {
        if (CROPPED_SIZES.has(name)) continue;
        if (size?.url && size.width) candidates.push({ url: size.url, width: size.width });
    }
    if (candidates.length === 0 && media.url && media.width) {
        candidates.push({ url: media.url, width: media.width });
    }

    const seen = new Set<string>();
    return candidates
        .sort((a, b) => a.width - b.width)
        .filter((variant) => {
            if (seen.has(variant.url)) return false;
            seen.add(variant.url);
            return true;
        });
}

/** First variant at least `width` wide, else the widest; `null` when there are none. */
export function pickMediaVariant(variants: MediaVariant[], width: number): MediaVariant | null {
    if (variants.length === 0) return null;
    return variants.find((variant) => variant.width >= width) ?? variants[variants.length - 1];
}

/**
 * Map every variant url — and the original url, even when it is not a candidate — to that
 * media's full variant list, so an image whose `src` is any rendition resolves to a srcset.
 */
export function buildVariantLookup(
    mediaList: Array<Media | number | null | undefined>
): Record<string, MediaVariant[]> {
    const lookup: Record<string, MediaVariant[]> = {};
    for (const media of mediaList) {
        const variants = getMediaVariants(media);
        if (variants.length === 0 || !media || typeof media === "number") continue;
        for (const variant of variants) lookup[variant.url] = variants;
        if (media.url) lookup[media.url] = variants;
    }
    return lookup;
}
